import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { EditablePortfolioBudgetCell } from '@/components/EditablePortfolioBudgetCell';
import { ReportColumnPicker } from '@/components/ReportColumnPicker';
import { useColumnOrder } from '@/components/workspace/useColumnOrder';
import { useMetricFilters } from '@/components/workspace/useMetricFilters';
import { useTableSort } from '@/components/workspace/useTableSort';
import { useWorkspaceFrozenLayout } from '@/components/workspace/workspaceStickyColumns';
import { WorkspaceExportButton } from '@/components/workspace/WorkspaceExportButton';
import { WorkspaceMetricFiltersPopover } from '@/components/workspace/WorkspaceMetricFiltersPopover';
import {
  WorkspaceOrderedHeaders,
  WorkspaceOrderedMetricCells,
  WorkspaceOrderedTotalsRow,
} from '@/components/workspace/WorkspaceOrderedHeaders';
import {
  FilterSearch,
  FilterSelect,
  SelectionCheckbox,
  WorkspaceCheckboxCell,
  WorkspaceMiddleCell,
  WorkspaceSection,
  WorkspaceTablePagination,
  WorkspaceTableShell,
  tableRowClass,
  workspaceTableClass,
} from '@/components/workspace/WorkspaceSection';
import { WorkspaceTableSelectionActions } from '@/components/workspace/WorkspaceTableSelectionActions';
import {
  PORTFOLIO_COLUMN_IDS,
  defaultColumnIds,
  portfolioEntityColumnsForDemo,
  workspaceColumnPickerOptions,
} from '@/components/workspace/workspaceColumnConfig';
import { formatWorkspaceRangeSubtitle } from '@/components/workspace/WorkspaceAdProductMetricsTable';
import { OutOfBudgetBoostSwitch } from '@/components/workspace/OutOfBudgetBoostSwitch';
import { WorkspaceOutOfBudgetBoostModal } from '@/components/workspace/WorkspaceOutOfBudgetBoostModal';
import { useServerPagination } from '@/hooks/useServerPagination';
import { useWorkspacePortfoliosPage } from '@/hooks/useWorkspaceEntityPages';
import {
  emptyCrossFilter,
  togglePortfolioFilter,
  type EntityCrossFilter,
  type EntityGraph,
  type WorkspaceColumnFilters,
} from '@/utils/entityCrossFilter';
import {
  metricsForPortfolio,
  totalsFromMetricRows,
  WORKSPACE_METRIC_COLUMNS,
  type WorkspaceMetricsMaps,
} from '@/utils/workspaceAdProductMetrics';
import {
  exportWorkspaceTableCsv,
  fetchAllWorkspaceEntityRows,
  workspaceExportColumns,
} from '@/api/workspaceCsvExport';

function PortfolioStateBadge({ state }: { state: string }) {
  const normalized = state.toUpperCase();
  const variant =
    normalized === 'ENABLED' ? 'success' : normalized === 'PAUSED' ? 'warning' : 'neutral';
  return <Badge variant={variant}>{normalized}</Badge>;
}

export function WorkspacePortfoliosSection({
  profileId,
  graph,
  columnFilters,
  onColumnFiltersChange,
  appliedFilter,
  pendingPortfolioIds,
  onChangePendingPortfolioIds,
  selection,
  showApplyFilter,
  onApplyFilter,
  onDiscardSelection,
  hasActiveCrossFilter,
  previousMetricsMaps,
  showTitle = true,
}: {
  profileId: string;
  graph: EntityGraph | null;
  columnFilters: Pick<WorkspaceColumnFilters, 'portfolioSearch' | 'portfolioState'>;
  onColumnFiltersChange: (patch: Partial<WorkspaceColumnFilters>) => void;
  appliedFilter: EntityCrossFilter;
  pendingPortfolioIds: string[];
  onChangePendingPortfolioIds: (update: (ids: string[]) => string[]) => void;
  selection: { selectionCount: number; hasPendingChanges: boolean; canApply: boolean };
  showApplyFilter: boolean;
  onApplyFilter?: () => void;
  onDiscardSelection: () => void;
  hasActiveCrossFilter: boolean;
  /** Previous-period report maps used for row and total comparisons. */
  previousMetricsMaps: WorkspaceMetricsMaps;
  /** False when the page already names the section (e.g. a selected tab). */
  showTitle?: boolean;
}) {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isDemo = Boolean(user?.is_demo);
  const queryClient = useQueryClient();
  const { range: rangeState } = useAppDateRange();
  const compareEnabled = rangeState.compareEnabled;
  const rangeSubtitle = formatWorkspaceRangeSubtitle(
    rangeState.current.startDate,
    rangeState.current.endDate,
    rangeState.previous.startDate,
    rangeState.previous.endDate,
    compareEnabled
  );

  const [outOfBudgetBoostPortfolio, setOutOfBudgetBoostPortfolio] = useState<{
    scope: 'portfolio';
    portfolioId: string;
    name: string;
  } | null>(null);

  const portfolioSort = useTableSort({ defaultSortBy: 'name' });
  const portfolioMetricFilters = useMetricFilters({
    tableId: 'portfolios',
    metrics: WORKSPACE_METRIC_COLUMNS,
  });
  const portfolioEntityColumns = useMemo(
    () => portfolioEntityColumnsForDemo(isDemo),
    [isDemo]
  );
  const portfolioColumnIds = useMemo(
    () => (isDemo ? defaultColumnIds(portfolioEntityColumns) : PORTFOLIO_COLUMN_IDS),
    [isDemo, portfolioEntityColumns]
  );
  const portfolioColumns = useColumnOrder(portfolioColumnIds);
  const portfolioFreeze = useWorkspaceFrozenLayout(
    portfolioColumns.frozenColumnIds,
    portfolioColumns.displayOrder.join('\0')
  );

  const portfolioPageResetKey = [
    columnFilters.portfolioSearch,
    isDemo ? 'ALL' : columnFilters.portfolioState,
    JSON.stringify(appliedFilter),
    rangeState.current.startDate,
    rangeState.current.endDate,
    portfolioSort.sortBy,
    portfolioSort.sortDir,
    portfolioMetricFilters.serialized,
  ].join('|');
  const portfolioPager = useServerPagination(portfolioPageResetKey);

  const portfoliosPage = useWorkspacePortfoliosPage({
    profileId,
    startDate: rangeState.current.startDate,
    endDate: rangeState.current.endDate,
    page: portfolioPager.page,
    pageSize: portfolioPager.pageSize,
    search: columnFilters.portfolioSearch,
    status: isDemo ? 'ALL' : columnFilters.portfolioState,
    appliedFilter,
    sortBy: portfolioSort.sortBy,
    sortDir: portfolioSort.sortDir,
    metricFilters: portfolioMetricFilters.payload,
    enabled: Boolean(profileId),
  });

  const portfoliosLoading = portfoliosPage.isPending;
  const allPortfoliosSelected =
    portfoliosPage.rows.length > 0 &&
    portfoliosPage.rows.every((p) => pendingPortfolioIds.includes(p.portfolioId));

  const portfolioCurrentTotals = totalsFromMetricRows(portfoliosPage.rows.map((r) => r.metrics));
  const portfolioPreviousTotals = totalsFromMetricRows(
    portfoliosPage.rows.map((r) => metricsForPortfolio(r.portfolioId, previousMetricsMaps))
  );

  const budgetByCampaignKey = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const campaign of graph?.campaigns ?? []) {
      map.set(
        campaign.campaignKey,
        typeof campaign.raw.budget === 'number' ? campaign.raw.budget : null
      );
    }
    return map;
  }, [graph]);

  const campaignNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const campaign of graph?.campaigns ?? []) {
      map.set(campaign.campaignId, campaign.name);
    }
    return map;
  }, [graph]);

  const exportPortfolios = useCallback(async () => {
    try {
      const fetched = await fetchAllWorkspaceEntityRows({
        profileId,
        entity: 'portfolios',
        startDate: rangeState.current.startDate,
        endDate: rangeState.current.endDate,
        columnFilters: {
          search: columnFilters.portfolioSearch || undefined,
          status:
            !isDemo && columnFilters.portfolioState !== 'ALL'
              ? columnFilters.portfolioState
              : undefined,
        },
        appliedFilter: appliedFilter as unknown as Record<string, unknown>,
        sortBy: portfolioSort.sortBy,
        sortDir: portfolioSort.sortDir,
        metricFilters: portfolioMetricFilters.payload,
      });
      const columns = workspaceExportColumns(portfolioColumns.visibleIds, portfolioEntityColumns);
      exportWorkspaceTableCsv({
        filenamePrefix: 'workspace_portfolios',
        startDate: rangeState.current.startDate,
        endDate: rangeState.current.endDate,
        columns,
        rows: fetched.rows,
        extras: {
          campaignNameById,
          budgetByCampaignKey,
        },
      });
      if (fetched.truncated) {
        showToast(
          `Exported first ${fetched.rows.length.toLocaleString()} of ${fetched.totalCount.toLocaleString()} rows`,
          'error'
        );
      } else {
        showToast(`Exported ${fetched.rows.length.toLocaleString()} rows`, 'success');
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to export table', 'error');
    }
  }, [
    appliedFilter,
    budgetByCampaignKey,
    campaignNameById,
    columnFilters.portfolioSearch,
    columnFilters.portfolioState,
    isDemo,
    portfolioColumns.visibleIds,
    portfolioEntityColumns,
    portfolioMetricFilters.payload,
    portfolioSort.sortBy,
    portfolioSort.sortDir,
    profileId,
    rangeState.current.startDate,
    rangeState.current.endDate,
    showToast,
  ]);

  return (
    <>
        <WorkspaceSection
          title={showTitle ? 'Portfolios' : undefined}
          countLabel={`${portfoliosPage.totalCount} total · ${rangeSubtitle}`}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <WorkspaceMetricFiltersPopover
                metrics={WORKSPACE_METRIC_COLUMNS}
                {...portfolioMetricFilters}
              />
              <WorkspaceExportButton onExport={() => void exportPortfolios()} />
              <ReportColumnPicker
                columns={workspaceColumnPickerOptions(portfolioEntityColumns)}
                selected={portfolioColumns.visibleIds}
                onChange={portfolioColumns.setVisibleIds}
                requiredColumnId="name"
                panelTitle="Visible columns"
              />
            </div>
          }
          toolbar={
            <div className="flex flex-wrap items-end gap-3">
              <FilterSearch
                id="workspace-portfolio-search"
                label="Portfolios"
                value={columnFilters.portfolioSearch}
                onChange={(v) => onColumnFiltersChange({ portfolioSearch: v })}
              />
              {!isDemo ? (
                <FilterSelect
                  id="workspace-portfolio-state"
                  label="State"
                  value={columnFilters.portfolioState}
                  onChange={(v) =>
                    onColumnFiltersChange({ portfolioState: v as typeof columnFilters.portfolioState })
                  }
                >
                  <option value="ALL">All</option>
                  <option value="ENABLED">Enabled</option>
                  <option value="PAUSED">Paused</option>
                </FilterSelect>
              ) : null}
              <WorkspaceTableSelectionActions
                entityLabel="portfolio"
                selectionCount={selection.selectionCount}
                hasPendingChanges={selection.hasPendingChanges}
                canApply={selection.canApply}
                onApply={() => onApplyFilter?.()}
                onClearSelection={onDiscardSelection}
                disabled={portfoliosLoading}
                showApplyFilter={showApplyFilter}
              />
            </div>
          }
        >
          <WorkspaceTableShell
            isEmpty={!portfoliosPage.isPending && portfoliosPage.rows.length === 0}
            isLoading={portfoliosLoading}
            emptyMessage={
              hasActiveCrossFilter
                ? 'No portfolios match the current selection.'
                : 'No portfolios for this account.'
            }
            footer={
              <WorkspaceTablePagination
                page={portfolioPager.page}
                pageSize={portfolioPager.pageSize}
                total={portfoliosPage.totalCount}
                onPageChange={portfolioPager.setPage}
                onPageSizeChange={portfolioPager.setPageSize}
                disabled={portfoliosLoading}
              />
            }
          >
            <table ref={portfolioFreeze.tableRef} className={workspaceTableClass.table}>
              <thead>
                <tr>
                  <WorkspaceCheckboxCell isHeader>
                    <SelectionCheckbox
                      checked={allPortfoliosSelected}
                      label="Select all visible portfolios"
                      disabled={portfoliosLoading}
                      onChange={(checked) => {
                        onChangePendingPortfolioIds((ids) => {
                          if (checked) {
                            const next = new Set(ids);
                            for (const row of portfoliosPage.rows) next.add(row.portfolioId);
                            return [...next];
                          }
                          const pageIds = new Set(portfoliosPage.rows.map((r) => r.portfolioId));
                          return ids.filter((id) => !pageIds.has(id));
                        });
                      }}
                    />
                  </WorkspaceCheckboxCell>
                  <WorkspaceOrderedHeaders
                    order={portfolioColumns.displayOrder}
                    entityColumns={portfolioEntityColumns}
                    sortBy={portfolioSort.sortBy}
                    sortDir={portfolioSort.sortDir}
                    onSort={portfolioSort.toggleSort}
                    onFreeze={portfolioColumns.freezeColumn}
                    frozenColumnIds={portfolioColumns.frozenColumnIds}
                    pinnedFrozenColumnId={portfolioColumns.pinnedFrozenColumnId}
                    frozenLeftById={portfolioFreeze.frozenLeftById}
                    onDragStartColumn={portfolioColumns.startDrag}
                    onDragHoverColumn={portfolioColumns.hoverReorder}
                    onDropColumn={portfolioColumns.commitDrop}
                    onDragEndColumn={portfolioColumns.endDrag}
                    activeDragColumnId={portfolioColumns.activeDragColumnId}
                    hoverColumnId={portfolioColumns.hoverColumnId}
                    hoverPosition={portfolioColumns.hoverPosition}
                    hoverInsertIndex={portfolioColumns.hoverInsertIndex}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {portfoliosPage.rows.map((row) => {
                  const selected = pendingPortfolioIds.includes(row.portfolioId);
                  const previousMetrics = metricsForPortfolio(row.portfolioId, previousMetricsMaps);
                  return (
                    <tr key={row.portfolioId} className={tableRowClass(selected)}>
                      <WorkspaceCheckboxCell>
                        <SelectionCheckbox
                          checked={selected}
                          label={`Select portfolio ${row.name}`}
                          disabled={portfoliosLoading}
                          onChange={(checked) =>
                            onChangePendingPortfolioIds((ids) => {
                              const next = togglePortfolioFilter(
                                { ...emptyCrossFilter(), portfolioIds: ids },
                                row.portfolioId,
                                checked
                              );
                              return next.portfolioIds;
                            })
                          }
                      />
                    </WorkspaceCheckboxCell>
                    <WorkspaceOrderedMetricCells
                        order={portfolioColumns.displayOrder}
                        frozenColumnIds={portfolioColumns.frozenColumnIds}
                        frozenLeftById={portfolioFreeze.frozenLeftById}
                        metrics={row.metrics}
                        previousMetrics={previousMetrics}
                        compareEnabled={compareEnabled}
                        isLoading={portfoliosLoading}
                        activeDragColumnId={portfolioColumns.activeDragColumnId}
                        hoverColumnId={portfolioColumns.hoverColumnId}
                        hoverPosition={portfolioColumns.hoverPosition}
                        renderEntityCell={(columnId, meta) => {
                          if (columnId === 'name') {
                            return (
                              <WorkspaceMiddleCell
                                style={meta.style}
                                className={`${meta.className} font-medium text-foreground`}
                                alignToTitle
                              >
                                {row.name}
                              </WorkspaceMiddleCell>
                            );
                          }
                          if (columnId === 'budget') {
                            return (
                              <td
                                style={meta.style} className={`${workspaceTableClass.td} ${meta.className} text-right tabular-nums`}
                              >
                                <EditablePortfolioBudgetCell
                                  profileId={profileId}
                                  portfolioId={row.portfolioId}
                                  budget={row.budget}
                                  portfolioLabel={row.name}
                                  onUpdated={() => {
                                    void portfoliosPage.refetch();
                                    void queryClient.invalidateQueries({
                                      queryKey: ['portfolios', profileId],
                                    });
                                  }}
                                />
                              </td>
                            );
                          }
                          if (columnId === 'state') {
                            return (
                              <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                                {row.state === '—' ? '—' : <PortfolioStateBadge state={row.state} />}
                              </WorkspaceMiddleCell>
                            );
                          }
                          if (columnId === 'outOfBudgetBoost') {
                            return (
                              <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                                <OutOfBudgetBoostSwitch
                                  enabled={row.outOfBudgetBoostEnabled === true}
                                  disabled={portfoliosLoading}
                                  onClick={() =>
                                    setOutOfBudgetBoostPortfolio({
                                      scope: 'portfolio',
                                      portfolioId: row.portfolioId,
                                      name: row.name,
                                    })
                                  }
                                />
                              </WorkspaceMiddleCell>
                            );
                          }
                          return null;
                        }}
                      />
                    </tr>
                  );
                })}
              </tbody>
              {portfoliosPage.rows.length > 0 ? (
                <tfoot>
                  <WorkspaceOrderedTotalsRow
                    order={portfolioColumns.displayOrder}
                    frozenColumnIds={portfolioColumns.frozenColumnIds}
                    frozenLeftById={portfolioFreeze.frozenLeftById}
                    current={portfolioCurrentTotals}
                    previous={portfolioPreviousTotals}
                    compareEnabled={compareEnabled}
                    isLoading={portfoliosLoading}
                  />
                </tfoot>
              ) : null}
            </table>
          </WorkspaceTableShell>
        </WorkspaceSection>

      {outOfBudgetBoostPortfolio ? (
        <WorkspaceOutOfBudgetBoostModal
          profileId={profileId}
          target={outOfBudgetBoostPortfolio}
          onClose={() => setOutOfBudgetBoostPortfolio(null)}
          onChanged={() => {
            showToast('Out of budget settings updated', 'success');
            void queryClient.invalidateQueries({
              queryKey: ['workspace-entity-page', 'portfolios', profileId],
            });
          }}
        />
      ) : null}
    </>
  );
}
